import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook for managing admin configuration sections.
 * Provides standardized fetch, save, and state management for config endpoints.
 *
 * @param {string} endpoint - API endpoint path (e.g., '/admin/config/smtp')
 * @param {object} initialState - Initial config state
 * @param {string} label - Human-readable label for alert messages (e.g., 'SMTP')
 * @returns {{ config, setConfig, saving, fetch, save }}
 */
function useConfigManager(endpoint, initialState, label) {
  const [config, setConfig] = useState(initialState);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const response = await api.get(endpoint);
      setConfig(response.data);
    } catch (error) {
      console.error(`Error fetching ${label} config:`, error);
    }
  }, [endpoint, label]);

  const save = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      await api.put(endpoint, config);
      alert(`${label} configuration saved successfully`);
      // Refetch to get server-normalized data
      try {
        const response = await api.get(endpoint);
        setConfig(response.data);
      } catch (refetchError) {
        console.error(`Error refetching ${label} config:`, refetchError);
      }
    } catch (error) {
      console.error(`Error saving ${label} config:`, error);
      alert(`Failed to save ${label} configuration: ` + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  }, [endpoint, config, label]);

  return { config, setConfig, saving, fetch, save };
}

export default useConfigManager;
